import { BaseItem } from '@algolia/autocomplete-core';
import { AutocompleteQuerySuggestionsHit } from '@algolia/autocomplete-plugin-query-suggestions/dist/esm/types';
import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {
  connectAutocomplete,
  connectSearchBox,
} from 'instantsearch.js/es/connectors';
import { highlight } from 'instantsearch.js/es/helpers';
import { index } from 'instantsearch.js/es/widgets';

import { renderSuggestionsIndexListItem } from '@/src/utilities/search/searchRenderFunctions';
import { generateSearchResultsPath } from '@/src/utilities/search/utils';

export function loadHomePageSearch(): void {
  const config = window['home-page-search'];

  if (!config) {
    return;
  }

  const {
    searchIndexName,
    suggestionsIndexName,
    applicationId,
    searchKey,
    searchContext,
  } = config;

  const algoliaClient = algoliasearch(applicationId, searchKey);

  const searchClient = {
    ...algoliaClient,
    search(requests: any[]) {
      return algoliaClient.search(requests);
    },
  };

  const search = instantsearch({
    indexName: searchIndexName,
    searchClient,
    insights: true,
  });

  // render functions:
  const renderAutocomplete = (
    renderOptions: {
      indices: object[];
      currentRefinement: string;
      refine: any; // should be a function
      widgetParams: any; // should be an object
    },
    isFirstRender: boolean
  ): void => {
    const { widgetParams } = renderOptions;

    if (isFirstRender) {
      const aaForm = document.createElement('form'); // aa-Form

      const aaPanel = document.createElement('div'); // dropdown panel

      const suggestionsList = document.createElement('div'); // contains 1 li per hit/suggestion
      const providerList = document.createElement('div');
      const locationList = document.createElement('div');
      const conditionList = document.createElement('div');

      suggestionsList.classList.add('suggestions-list');
      providerList.classList.add('provider-list');
      locationList.classList.add('location-list');
      conditionList.classList.add('condition-list');

      aaPanel.appendChild(suggestionsList);
      aaPanel.appendChild(providerList);
      aaPanel.appendChild(locationList);
      aaPanel.appendChild(conditionList);

      const aaInputWrapperPrefix = document.createElement('div'); // aa-InputWrapperPrefix
      const aaLabel = document.createElement('label'); // aa-Label
      aaLabel.setAttribute('class', 'block font-semibold mb-4');
      aaLabel.setAttribute('for', 'urmcQuery');
      aaLabel.innerHTML = `Search UR Medicine <span class="font-normal text-gray-700 italic block sm:ml-3 sm:inline-block">Providers, locations, conditions, etc.</span>`;

      const aaExtendedSearchbox = document.createElement('div'); // aa-ExtendedSearchbox
      const aaClearButton = document.createElement('button'); // aa-ClearButton
      aaClearButton.setAttribute('aria-label', 'Clear search form');

      const aaSubmitButton = document.createElement(
        'button'
      ) as HTMLButtonElement; // aa-SubmitButton

      const aaLoadingIndicator = document.createElement('div'); // aa-LoadingIndicator
      const aaLoadingIcon = document.createElement('svg'); // aa-LoadingIcon

      const aaInput = document.createElement('input'); // aa-Input

      const aaInputWrapperSuffix = document.createElement('div'); // aa-InputWrapperSuffix

      aaInput.addEventListener('focus', (event) => {
        const eventTarget = event.target as HTMLInputElement;

        if (eventTarget.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        }

        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        }
      });

      aaInput.addEventListener('input', (event) => {
        const eventTarget = event.target as HTMLInputElement;

        if (eventTarget.value.length > 1) {
          autocompleteSearch(eventTarget.value);
          aaPanel.classList.remove('hidden');
        } else {
          aaPanel.classList.add('hidden');
        }

        if (aaInput.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        } else {
          aaClearButton.classList.add('hidden');
        }
      });

      let panelIsFocused = false;
      aaPanel.addEventListener('mousedown', (event) => {
        event.preventDefault();

        panelIsFocused = true;
      });

      aaClearButton.addEventListener('mousedown', (event) => {
        event.preventDefault();
        aaPanel.classList.add('hidden');
      });

      aaInput.addEventListener('blur', () => {
        searchContext.searchTerm = aaInput.value;
        autocompleteSearch(aaInput.value);

        if (!panelIsFocused) {
          aaPanel.classList.add('hidden');
        }
      });

      // add hover state on mouse enter
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseenter',
        (event) => {
          const target = event.target as HTMLElement;

          if (target.tagName === 'A') {
            target.classList.add('bg-stone-100', 'hover:underline');
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // remove background color on mouse leave UNLESS it's the currently focused element
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseleave',
        (event) => {
          const target = event.target as HTMLElement;
          const currentFocus = document.querySelector('#selected-query');

          if (target.tagName === 'A') {
            if (target !== currentFocus) {
              target.classList.remove('bg-stone-100');
            }
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // arrow key navigation
      aaInput.addEventListener('keydown', (event: KeyboardEvent) => {
        const focusableEls = Array.prototype.slice.call(
          aaPanel.querySelectorAll('a')
        );

        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        const currentFocus = document.querySelector('#selected-query');
        let currentIndex = focusableEls.indexOf(currentFocus);
        const nextFocusableEl = focusableEls[currentIndex + 1];
        const prevFocusableEl = focusableEls[currentIndex - 1];

        if (event.key === 'ArrowDown' && aaInput.value.length > 1) {
          event.preventDefault();

          if (
            currentIndex === -1 ||
            currentFocus?.hasAttribute('data-app-autocomplete-view-all-link')
          ) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex++;

          if (!nextFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            aaPanel.scroll({ top: 0, behavior: 'smooth' });

            aaInput.value = searchContext.searchTerm;
          } else {
            nextFocusableEl.classList.add('bg-stone-100');

            nextFocusableEl.setAttribute('id', 'selected-query');
            nextFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            if (
              nextFocusableEl?.hasAttribute(
                'data-app-autocomplete-view-all-link'
              )
            ) {
              aaInput.value = searchContext.searchTerm;
            } else {
              aaInput.value = nextFocusableEl.textContent.trim();
            }
          }
        }

        if (event.key === 'ArrowUp' && aaInput.value.length > 1) {
          event.preventDefault();

          if (
            currentIndex === -1 ||
            currentFocus?.hasAttribute('data-app-autocomplete-view-all-link')
          ) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex--;

          if (!prevFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            if (!currentFocus) {
              lastFocusableEl.classList.add('bg-stone-100');

              lastFocusableEl.setAttribute('id', 'selected-query');

              lastFocusableEl.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });
              if (
                lastFocusableEl?.hasAttribute(
                  'data-app-autocomplete-view-all-link'
                )
              ) {
                aaInput.value = searchContext.searchTerm;
              } else {
                aaInput.value = lastFocusableEl.textContent.trim();
              }
            } else {
              aaInput.value = searchContext.searchTerm;
            }
          } else {
            prevFocusableEl.classList.add('bg-stone-100');

            prevFocusableEl.setAttribute('id', 'selected-query');
            prevFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
            if (
              prevFocusableEl?.hasAttribute(
                'data-app-autocomplete-view-all-link'
              )
            ) {
              aaInput.value = searchContext.searchTerm;
            } else {
              aaInput.value = prevFocusableEl.textContent.trim();
            }
          }
        }

        if (event.key === 'Enter') {
          event.preventDefault();

          if (
            currentFocus?.hasAttribute('data-app-autocomplete-result') ||
            currentFocus?.hasAttribute('data-app-autocomplete-view-all-link')
          ) {
            const url = currentFocus.getAttribute('href') as string;

            window.location.href = url;
          } else {
            if (currentFocus) {
              const selectedQuery = currentFocus.textContent?.trim() as string;
              aaInput.value = selectedQuery;
            }
            const query = aaInput.value;

            const url = generateSearchResultsPath(query).toString();

            window.location.href = url;
          }
        }
      });

      aaClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        aaInput.value = '';
        aaClearButton.classList.add('hidden');
      });

      // autocomplete panel section
      // aaPanel, ul
      aaPanel.setAttribute(
        'class',
        'aa-Panel z-50 w-full hidden search-autocomplete-panel'
      );

      aaForm.setAttribute('class', 'lg:flex gap-4 items-end');

      aaLoadingIndicator.setAttribute('class', 'aa-LoadingIndicator');
      aaLoadingIcon.setAttribute('class', 'aa-LoadingIcon');

      // prefix section
      // aaInput, aaLabel, aaClearButton
      aaExtendedSearchbox.setAttribute(
        'class',
        'aa-ExtendedSearchbox flex items-center relative'
      );

      const toggleQueryClearButton = aaInput.value.length > 0 ? '' : 'hidden';

      aaClearButton.setAttribute(
        'class',
        `-ml-7 ${toggleQueryClearButton} group absolute right-3`
      );

      aaClearButton.innerHTML = `
          <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
          </svg>`;

      aaInputWrapperPrefix.setAttribute(
        'class',
        'aaInputWrapperPrefix flex-1 relative'
      );

      aaInput.setAttribute(
        'class',
        'border rounded w-full p-3 leading-snug border-stone-400'
      );
      aaInput.setAttribute('id', 'urmcQuery');
      aaInput.setAttribute('name', 'urmcQuery');

      aaInput.setAttribute('type', 'text');
      aaInput.setAttribute('autocomplete', 'off');

      // suffix section
      // aaSubmitButton
      aaSubmitButton.setAttribute(
        'class',
        'btn btn-primary my-4 w-full max-w-none md:my-0'
      );
      aaSubmitButton.setAttribute('aria-label', 'Search website');
      aaSubmitButton.innerHTML = 'Search';

      aaForm.onsubmit = (event) => {
        event.preventDefault();
        const query = aaInput.value;

        const url = generateSearchResultsPath(query).toString();

        window.location.href = url;
      };

      if (
        typeof widgetParams.container === 'object' &&
        widgetParams.container !== null &&
        'appendChild' in widgetParams.container
      ) {
        widgetParams.container.appendChild(aaForm);
        widgetParams.container.appendChild(aaLoadingIndicator);

        // aaForm section
        aaForm.appendChild(aaInputWrapperPrefix);
        aaForm.appendChild(aaInputWrapperSuffix);

        // aaExtendedSearchbox
        aaExtendedSearchbox.appendChild(aaInput);
        aaExtendedSearchbox.appendChild(aaClearButton);

        // prefix
        aaInputWrapperPrefix.appendChild(aaLabel);
        aaInputWrapperPrefix.appendChild(aaExtendedSearchbox);
        aaInputWrapperPrefix.appendChild(aaPanel);

        // wrapper

        // suffix
        aaInputWrapperSuffix.appendChild(aaSubmitButton);
      }

      // end of isFirstRender conditional
    }
  };

  const renderProviderIndexListItem = ({ indexId, query, hits }) => `
        <div class="py-2 px-5 uppercase text-sm font-bold ${
          hits.length > 0 ? 'has-results' : 'hidden'
        }">Results for Providers</div>
        <ol class="list-none">
          ${hits
            .map(
              (hit: any) =>
                `
                  <li>
                    <a data-app-autocomplete-result class="py-4 px-5 block w-full" tabindex="-1"
                    href="${hit['Url']}"
                    >
                      ${highlight({
                        attribute: 'Title',
                        hit,
                      })}
                    </a>
                  </li>
                `
            )
            .join('')}
        </ol>

        <a data-app-autocomplete-view-all-link class="py-4 px-5 block w-full text-blue-600 ${
          hits.length > 0 ? 'has-results' : 'hidden'
        }" href="/people/search?term=${
    query || ''
  }">View all provider results</a>
        `;

  const renderLocationIndexListItem = ({ indexId, query, hits }) => `
        <div class="py-2 px-5 uppercase text-sm font-bold ${
          hits.length > 0 ? 'has-results' : 'hidden'
        }">Results for locations</div>
        <ol class="list-none">
          ${hits
            .map(
              (hit: any) =>
                `
                  <li>
                    <a data-app-autocomplete-result class="py-4 px-5 block w-full" tabindex="-1"
                    href="${hit['Url']}"
                    >
                      ${highlight({
                        attribute: 'Title',
                        hit,
                        highlightedTagName: 'span',
                      })} ${
                  hit['Address']?.Town ? `- ${hit['Address']?.Town}` : ''
                }
                    </a>
                  </li>
                `
            )
            .join('')}
        </ol>

        <a data-app-autocomplete-view-all-link class="py-4 px-5 block w-full text-blue-600 ${
          hits.length > 0 ? 'has-results' : 'hidden'
        }" href="/locations/search?term=${
    query || ''
  }">View all location results</a>
        `;

  const renderConditionIndexListItem = ({ indexId, query, hits }) => `
        <div class="py-2 px-5 uppercase text-sm font-bold ${
          hits.length > 0 ? 'has-results' : 'hidden'
        }">Results for Conditions & Treatments</div>
        <ol class="list-none">
          ${hits
            .map(
              (hit: any) =>
                `
                  <li>
                    <a data-app-autocomplete-result class="py-4 px-5 block w-full" tabindex="-1"
                    href="${hit['Url']}"
                    >
                      ${highlight({
                        attribute: 'Title',
                        hit,
                        highlightedTagName: 'span',
                      })}
                    </a>
                  </li>
                `
            )
            .join('')}
        </ol>

        <a data-app-autocomplete-view-all-link class="py-4 px-5 block w-full text-blue-600 ${
          hits.length > 0 ? 'has-results' : 'hidden'
        }" href="/conditions-and-treatments/search?term=${
    query || ''
  }">View all condition & treatment results</a>

        <a data-app-autocomplete-view-all-link class="py-4 px-5 block w-full text-blue-600 ${
          hits.length > 0 ? 'has-results' : 'hidden'
        }" href="/search?term=${query || ''}">View all search results</a>
        `;

  // utils:
  function autocompleteSearch(query: string) {
    const suggestionsIndex = searchClient.initIndex(suggestionsIndexName);
    const resultsIndex = searchClient.initIndex(searchIndexName);

    suggestionsIndex
      .search(query, {
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const suggestionsList = document.querySelector(
          '.suggestions-list'
        ) as HTMLElement;

        suggestionsList.innerHTML = renderSuggestionsIndexListItem({
          indexId: suggestionsIndexName,
          hits: hits,
        });
      });

    resultsIndex
      .search(query, {
        filters: 'ClassName:"URMC.ProviderPage"',
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const resultsList = document.querySelector(
          '.provider-list'
        ) as HTMLElement;

        resultsList.innerHTML = renderProviderIndexListItem({
          indexId: searchIndexName,
          query: query,
          hits: hits,
        });
      });

    resultsIndex
      .search(query, {
        filters: 'ClassName:"URMC.LocationPage"',
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const resultsList = document.querySelector(
          '.location-list'
        ) as HTMLElement;

        resultsList.innerHTML = renderLocationIndexListItem({
          indexId: searchIndexName,
          query: query,
          hits: hits,
        });
      });

    resultsIndex
      .search(query, {
        filters: 'ClassName:"URMC.ConditionPage"',
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const resultsList = document.querySelector(
          '.condition-list'
        ) as HTMLElement;

        resultsList.innerHTML = renderConditionIndexListItem({
          indexId: searchIndexName,
          query: query,
          hits: hits,
        });
      });
  }

  const virtualSearchBox = connectSearchBox(() => null);
  const customAutocomplete = connectAutocomplete(renderAutocomplete);

  search.addWidgets([
    virtualSearchBox({}),
    customAutocomplete({
      container: document.querySelector('[data-app-search-box]'),
    }),
  ]);

  search.start();
}

type ConditionSearchResult = BaseItem & {
  Title: string;
  Url: string;
};

type SuggestionResult = BaseItem & AutocompleteQuerySuggestionsHit;
